// Checkout.tsx
import React, { useState, useEffect, useRef } from 'react';
import type { TourProps } from 'antd';
import { getAuthHeaders } from './utils/authHeaders';
import { auth } from './firebaseAuth';
import { resolveFrontendApiBase } from './utils/apiBase';
import {
  Affix,
  Alert,
  Button,
  Carousel,
  Collapse,
  Modal,
  Steps,
  Tag,
  Tooltip,
  Input,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  LeftOutlined,
  LockOutlined,
  RightOutlined,
  StarFilled,
  UserOutlined,
  ExclamationCircleOutlined,
  DollarCircleOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  TranslationOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingCard from './components/LoadingCard';
import AppPageHeader from './components/AppPageHeader';
import type { CarouselRef } from 'antd/es/carousel';
import * as fbPixel from './utils/fbPixel';
import { getFacebookCookies } from './utils/trackingCookies';
import { generateRevenueFunnelId, trackRevenuePricingIntent } from './utils/revenueHealth';
import axios from './utils/accountApi';
import { getStripePromise } from './utils/stripe';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import './Checkout.css';

const { Step } = Steps;
const { Panel } = Collapse;

const API_BASE = resolveFrontendApiBase();

/* ---------- Types ---------- */
interface CheckoutProps {
  isAuth: boolean;
  user: any;
  usersSharedState: { userID?: string };
  showLoginModal: () => void;
  setCheckoutPlan: (plan: string | null) => void;
}

type PlanKey =
  | 'starter'
  | 'storyteller'
  | 'wordsmith'
  | 'novelist'
  | 'bestseller'
  | 'publisher'
  | 'polyglot';

type BillingInterval = 'monthly' | 'yearly';

interface PlanDetail {
  key: PlanKey;
  name: string;
  originalPrice: string;     // e.g. "$4.99"
  credits: string;           // e.g. "10,000 credits"
  stripePriceId: string;     // Stripe recurring price for full list price
  stripeProductId?: string;
  billingInterval?: BillingInterval;
  intervalMonths?: number;
  creditsPerInterval?: number;
  monthlyEquivalentCredits?: number;
  currency?: string;
  defaultCouponId?: string;  // Optional default coupon for your evergreen promo
  features: string[];
}

interface EffectivePlan {
  base: PlanDetail;
  displayPrice: string;      // What we show on page and send to Pixel
  savingsPercent: number;    // Computed vs original
  couponToApply?: string;    // What we send to Stripe (from URL or default)
}

interface Testimonial {
  quote: string;
  author: string;
  revenue?: string;
  rating: number;
}

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

/* ---------- Data ---------- */
const REFERRAL_STORAGE_KEY = 'scribeshadow_referral_code';

export const sanitizeReferralCode = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);

type FacebookCookies = {
  fbp?: string | null;
  fbc?: string | null;
};

interface CheckoutSessionBodyInput {
  plan: PlanDetail;
  effectivePlan: EffectivePlan;
  userId: string | null | undefined;
  email: string | null | undefined;
  source: string;
  couponParam: string | null;
  successUrl: string;
  cancelUrl: string;
  cookies: FacebookCookies;
  initiateCheckoutEventId: string;
  anonymousFunnelId: string;
  clientUserAgent: string;
  eventSourceUrl: string;
  fbTestCode?: string | null;
}

export const buildCheckoutSessionBody = ({
  plan,
  effectivePlan,
  userId,
  email,
  source,
  couponParam,
  successUrl,
  cancelUrl,
  cookies,
  initiateCheckoutEventId,
  anonymousFunnelId,
  clientUserAgent,
  eventSourceUrl,
  fbTestCode,
}: CheckoutSessionBodyInput) => ({
  priceId: plan.stripePriceId,
  userId: userId ?? null,
  email: email ?? null,
  successUrl,
  cancelUrl,
  metadata: {
    source,
    plan: plan.key,
    planKey: plan.key,
    billingInterval: plan.billingInterval,
    intervalMonths: plan.intervalMonths,
    creditsPerInterval: plan.creditsPerInterval,
    monthlyEquivalentCredits: plan.monthlyEquivalentCredits,
    productId: plan.stripeProductId,
    currency: plan.currency,
    initiateCheckoutEventId,
    anonymousFunnelId,
    promo_price: effectivePlan.displayPrice,
    savings_percent: `${effectivePlan.savingsPercent}`,
    coupon_from_url: couponParam || '',
  },
  couponId: effectivePlan.couponToApply,
  fbp: cookies.fbp ?? undefined,
  fbc: cookies.fbc ?? undefined,
  clientUserAgent,
  eventSourceUrl,
  fb_test_code: fbTestCode || undefined,
});

const oldTestimonials: Testimonial[] = [
  {
    quote:
      "Thanks to ScribeShadow, I've added an additional 50 000 USD to my income and I did it all with my backlist.",
    author: 'Contemporary Dark Romance Author',
    revenue: '$50,000+ added revenue',
    rating: 5,
  },
  {
    quote:
      'For translations, I rely on ScribeShadow tailored for fiction. My German audiobook releases are rated higher than the American originals.',
    author: 'Derek Slaton – Horror Author',
    revenue: 'Higher ratings than originals',
    rating: 5,
  },
  {
    quote:
      'French and German translations are far out-pacing my US revenue. Rank rises with modest ad spend.',
    author: 'Thriller Author',
    revenue: 'Outpacing US revenue',
    rating: 5,
  },
  {
    quote: 'I make most of my money now from Europe.',
    author: 'Historical Romance Author',
    revenue: 'Majority income from EU',
    rating: 5,
  },
];

// Updated testimonials
const testimonials: Testimonial[] = [
  {
    quote:
      "I translated my entire 1.3-million-word series into French and German - they paid for themselves within 2 months and are now making more money than some of my English-speaking markets on Amazon. A great investment! Can't wait to do more languages.",
    author: 'Sara Creasy',
    rating: 5,
  },
  {
    quote:
      "I've been a full-time, self-published author for the last 11 years. It's been an exciting career that's had plenty of ups and downs. The last 18 months have been the toughest of all, and it was getting to the stage where writing books for a living no longer seemed sustainable. ScribeShadow has changed all that. Translating my books into multiple languages has made me very optimistic about the future, and, for the first time in a while, I feel hopeful that things are on the up once again.",
    author: 'George Mahood',
    rating: 5,
  },
  {
    quote:
      "I tried out ScribeShadow earlier this year after reading comments about it on an author group. I was initially worried to have my books translated. Much less by AI. But now I can't look back. In the past, translating my books was a daunting task. Unthinkable. But now, I've translated most of my back-list into German and it now makes up 75% of my income. I can't wait to finish translating my back-list into German, and then try out other languages.",
    author: 'Nicholas Woode-Smith',
    rating: 5,
  },
  {
    quote:
      "I've translated five of my series into German and am thrilled with the sales and page reads I'm getting! I paid big bucks for a human translator and didn't make my money back. (Still haven't, lol). ScribeShadow has enabled me to share my stories with others around the globe. As soon as I finish my German translations, I plan to do both Dutch and Italian! Thank you ScribeShadow for providing a viable financial option for translations!",
    author: 'Laura Scott',
    rating: 5,
  },
];

const faqItems = [
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes. Cancel anytime with no penalties. Your credits remain until the end of your billing period.',
  },
  {
    question: 'What if I need more credits?',
    answer: 'You can purchase additional credit packs anytime or upgrade your plan for better value.',
  },
  {
    question: 'Is my payment secure?',
    answer: 'Yes, we use Stripe for secure, encrypted payments. We never store your card details.',
  },
  {
    question: 'How quickly can I start translating?',
    answer: 'Immediately. Your credits are available as soon as payment is processed.',
  },
];

/* ----- Canonical plan table (list price + Stripe Price IDs) ----- */
// Static details for core plans. Additional plans (bestseller/publisher/polyglot)
// are resolved dynamically from the backend to avoid hardcoding their Stripe IDs here.
const planDetails: Partial<Record<PlanKey, PlanDetail>> = {
  starter: {
    key: 'starter',
    name: 'Inkling Plan',
    originalPrice: '$4.99',
    credits: '10,000 credits',
    stripePriceId: 'price_1OsH2TEj6HHcTsCP6JHIK4oX',
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 10000,
    monthlyEquivalentCredits: 10000,
    currency: 'USD',
    defaultCouponId: 'TAtQVA1k',
    features: [
      'Translate full novels in minutes',
      'Translate & Optimise metadata and blurbs',
      'Language specific keyword suggestions',
      '5,000 bonus credits when you join',
      'Keep 100% of your royalties',
    ],
  },
  storyteller: {
    key: 'storyteller',
    name: 'Storyteller Plan',
    originalPrice: '$24.99',
    credits: '55,000 credits',
    stripePriceId: 'price_1OsH1VEj6HHcTsCP6Ap1gN2Z', 
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 55000,
    monthlyEquivalentCredits: 55000,
    currency: 'USD',
    defaultCouponId: '4uckJjFm',
    features: [
      'Translate full novels in minutes',
      'Translate & Optimise metadata and blurbs',
      'Language specific keyword suggestions',
      '5,000 bonus credits when you join',
      'Keep 100% of your royalties',
    ],
  },
  wordsmith: {
    key: 'wordsmith',
    name: 'Wordsmith Plan',
    originalPrice: '$49.99',
    credits: '120,000 credits',
    stripePriceId: 'price_1OsGziEj6HHcTsCPeV5JB9yG', 
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 120000,
    monthlyEquivalentCredits: 120000,
    currency: 'USD',
    defaultCouponId: '4uckJjFm',
    features: [
      'Translate full novels in minutes',
      'Translate & Optimise metadata and blurbs',
      'Language specific keyword suggestions',
      '5,000 bonus credits when you join',
      'Keep 100% of your royalties',
    ],
  },
  novelist: {
    key: 'novelist',
    name: 'Novelist Plan',
    originalPrice: '$99.99',
    credits: '250,000 credits',
    stripePriceId: 'price_1OsGypEj6HHcTsCPxdhLKb6O', 
    billingInterval: 'monthly',
    intervalMonths: 1,
    creditsPerInterval: 250000,
    monthlyEquivalentCredits: 250000,
    currency: 'USD',
    defaultCouponId: '4uckJjFm',
    features: [
      'Translate full novels in minutes',
      'Translate & Optimise metadata and blurbs',
      'Language specific keyword suggestions',
      '5,000 bonus credits when you join',
      'Keep 100% of your royalties',
    ],
  },
};

/* ---------- Utils ---------- */
const toNumber = (usd: string): number => {
  const n = parseFloat(usd.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const toUsd = (n: number): string => `$${n.toFixed(2)}`;

const clampSavings = (percent: number): number =>
  Math.max(0, Math.min(95, Math.round(percent))); // keep it sane

const computeEffectivePlan = (
  base: PlanDetail,
  promoPriceParam: string | null,
  couponParam: string | null
): EffectivePlan => {
  const original = toNumber(base.originalPrice);

  // If a price param exists, use it for display and pixel tracking
  const displayPriceNum =
    promoPriceParam && Number.isFinite(parseFloat(promoPriceParam))
      ? parseFloat(promoPriceParam)
      : original;

  const savingsPercent =
    displayPriceNum < original && original > 0
      ? clampSavings(((original - displayPriceNum) / original) * 100)
      : 0;

  // Decide which coupon to send to Stripe
  // Priority: explicit coupon in URL, else defaultCouponId if discounted, else undefined
  let couponToApply: string | undefined = undefined;
  if (couponParam?.trim()) {
    couponToApply = couponParam.trim();
  } else if (displayPriceNum < original && base.defaultCouponId) {
    couponToApply = base.defaultCouponId;
  } else if (promoPriceParam && base.defaultCouponId) {
    // When a promo price is explicitly provided but original is unknown (e.g., dynamic plans), apply default coupon.
    couponToApply = base.defaultCouponId;
  }

  return {
    base,
    displayPrice: toUsd(displayPriceNum),
    savingsPercent,
    couponToApply,
  };
};

/* ---------- Component ---------- */
const Checkout: React.FC<CheckoutProps> = ({
  isAuth,
  user,
  usersSharedState,
  showLoginModal,
  setCheckoutPlan,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const planParam = (searchParams.get('plan') || 'starter').toLowerCase() as PlanKey;
  const source = searchParams.get('source') || 'landing';
  const promoPriceParam = searchParams.get('price');   // optional, numeric string like "1.99"
  const couponParam = searchParams.get('coupon');      // optional Stripe coupon id
  const fbTestCode = searchParams.get('fb_test_code'); // optional: route CAPI events to Test Events
  const referralParam = searchParams.get('ref');

  // Resolve plan details (static for core plans, dynamic for extended plans)
  const [canonicalPlan, setCanonicalPlan] = useState<PlanDetail | null>(null);
  const effective: EffectivePlan | null =
    canonicalPlan ? computeEffectivePlan(canonicalPlan, promoPriceParam, couponParam) : null;

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isEligibleForOffer, setIsEligibleForOffer] = useState<boolean>(true);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState<boolean>(true);
  const [guestEmail, setGuestEmail] = useState<string>('');
  const [referralCodeInput, setReferralCodeInput] = useState<string>('');
  const [referralApplyState, setReferralApplyState] = useState<'idle' | 'applying' | 'applied' | 'error'>('idle');
  const [referralMessage, setReferralMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const checkoutTour = useGuidedTour({
    userId: usersSharedState?.userID || user?.uid,
    apiBase: API_BASE,
    flag: 'checkoutTourSeen',
    enabled: true,
    autoStart: false,
  });
  const checkoutTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Confirm the selected plan',
      description: renderAppTourPanel('The checkout header summarizes the plan, today’s price, renewal price, and included credits.'),
      target: tourTarget('checkout-plan-summary'),
    },
    {
      title: 'Check readiness',
      description: renderAppTourPanel('Readiness shows account, plan, referral, eligibility, and payment status before Stripe opens.'),
      target: tourTarget('checkout-readiness'),
    },
    {
      title: 'Enter account and referral details',
      description: renderAppTourPanel('Guests enter an account email, and anyone can add an optional affiliate code before checkout.'),
      target: tourTarget('checkout-action-bar'),
    },
    {
      title: 'Start secure payment',
      description: renderAppTourPanel('The primary action creates a secure Stripe checkout session for the selected plan.'),
      target: tourTarget('checkout-primary-action', 'checkout-action-bar'),
    },
  ]), [user?.uid, usersSharedState?.userID]);
  const carouselRef = useRef<CarouselRef | null>(null);
  const ctaBarRef = useRef<HTMLDivElement | null>(null);
  const [ctaBarHeight, setCtaBarHeight] = useState(0);
  const referralApplyUserRef = useRef<string | null>(null);
  const referralInitRef = useRef<boolean>(false);
  const lastReferralParamRef = useRef<string | null>(null);

  const sanitizedReferralParam = referralParam ? sanitizeReferralCode(referralParam) : '';

  useEffect(() => {
    if (!referralInitRef.current) {
      referralInitRef.current = true;

      let initialCode = sanitizedReferralParam;
      if (initialCode) {
        try {
          window.localStorage.setItem(REFERRAL_STORAGE_KEY, initialCode);
        } catch (error) {
          // ignore storage errors
        }
      } else if (typeof window !== 'undefined') {
        try {
          const stored = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
          if (stored) {
            initialCode = sanitizeReferralCode(stored);
          }
        } catch (error) {
          // ignore storage errors
        }
      }

      if (initialCode) {
        setReferralCodeInput(initialCode);
        if (sanitizedReferralParam) {
          lastReferralParamRef.current = sanitizedReferralParam;
        }
      }
      return;
    }

    if (sanitizedReferralParam && sanitizedReferralParam !== lastReferralParamRef.current) {
      setReferralCodeInput(sanitizedReferralParam);
      try {
        window.localStorage.setItem(REFERRAL_STORAGE_KEY, sanitizedReferralParam);
      } catch (error) {
        // ignore storage errors
      }
      lastReferralParamRef.current = sanitizedReferralParam;
    }
  }, [sanitizedReferralParam, referralCodeInput]);

  useEffect(() => {
    if (!referralInitRef.current || typeof window === 'undefined') {
      return;
    }
    try {
      if (referralCodeInput) {
        window.localStorage.setItem(REFERRAL_STORAGE_KEY, referralCodeInput);
      } else {
        window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
      }
    } catch (error) {
      // ignore storage errors
    }
  }, [referralCodeInput]);

  const handleReferralCodeFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeReferralCode(event.target.value);
    setReferralCodeInput(sanitized);
    setReferralApplyState('idle');
    setReferralMessage(null);
    referralApplyUserRef.current = null;
  };

  useEffect(() => {
    if (!isAuth) {
      return;
    }
    const userId = usersSharedState.userID;
    if (!userId || !referralCodeInput || !referralInitRef.current) {
      return;
    }
    if (referralApplyState === 'applying') {
      return;
    }

    const key = `${userId}:${referralCodeInput}`;
    if (referralApplyUserRef.current === key && referralApplyState !== 'idle') {
      return;
    }

    let cancelled = false;

    const applyReferral = async () => {
      setReferralApplyState('applying');
      setReferralMessage(null);
      referralApplyUserRef.current = key;
      try {
        await axios.post(`${API_BASE}/referrals/apply`, {
          userID: userId,
          referralCode: referralCodeInput,
        });
        if (cancelled) return;
        setReferralApplyState('applied');
        setReferralMessage({
          type: 'success',
          text: 'Referral code saved to your account. Credits unlock after your first payment.',
        });
        try {
          window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
        } catch (error) {
          // ignore
        }
        message.success('Referral code saved to your account.');
      } catch (error: unknown) {
        if (cancelled) return;
        let messageText = 'We could not apply that referral code.';
        let messageType: 'error' | 'info' = 'error';
        let keepKey = false;

        if (axios.isAxiosError(error) && error.response) {
          const status = error.response.status;
          const detailRaw = (error.response.data as { detail?: string })?.detail;
          const detail = detailRaw ? String(detailRaw) : '';
          const detailLower = detail.toLowerCase();

          if (status === 404) {
            messageText = 'Referral code not found.';
          } else if (status === 409) {
            messageText = 'A referral code is already linked to this account.';
            messageType = 'info';
            keepKey = true;
          } else if (status === 400 && detailLower.includes('before completing a purchase')) {
            messageText = 'Referral codes must be applied before your first purchase.';
            keepKey = true;
          } else if (detail) {
            messageText = detail;
          }
        }

        if (messageType === 'info') {
          setReferralApplyState('applied');
          message.info(messageText);
        } else {
          setReferralApplyState('error');
          message.error(messageText);
        }
        setReferralMessage({ type: messageType, text: messageText });
        if (keepKey) {
          try {
            window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
          } catch (storageError) {
            // ignore
          }
        }
        referralApplyUserRef.current = key;
      }
    };

    applyReferral();

    return () => {
      cancelled = true;
    };
  }, [isAuth, usersSharedState.userID, referralCodeInput, referralApplyState]);

  // Mobile detection for CTA label and Collapse behavior
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTinyMobile, setIsTinyMobile] = useState<boolean>(false);
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setIsMobile(w < 576);
      setIsTinyMobile(w < 360);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Default coupon mapping for extended plans
  const getDefaultCouponForKey = (key: PlanKey): string | undefined => {
    if (key === 'storyteller' || key === 'wordsmith' || key === 'novelist' || key === 'bestseller') {
      return '4uckJjFm'; // 50% off
    }
    if (key === 'publisher' || key === 'polyglot') {
      return 'hzAspJ0n'; // 30% off
    }
    return undefined;
  };

  // Load plan details (static/dynamic based on plan key)
  useEffect(() => {
    let cancelled = false;

    const loadPlan = async () => {
      const staticPlan = (planDetails as Record<string, PlanDetail | undefined>)[planParam];
      if (staticPlan) {
        if (!cancelled) setCanonicalPlan(staticPlan);
        return;
      }

      try {
        const resp = await fetch(`${API_BASE}/get-subscription-plans`);
        if (resp.ok) {
          const data = await resp.json();
          const plans: any[] = Array.isArray(data?.subscriptionPlans) ? data.subscriptionPlans : [];
          const keyToName: Record<PlanKey, string> = {
            starter: 'Inkling',
            storyteller: 'Storyteller',
            wordsmith: 'Wordsmith',
            novelist: 'Novelist',
            bestseller: 'Bestseller',
            publisher: 'Publisher',
            polyglot: 'Polyglot',
          } as const;
          const targetName = (keyToName[planParam] || '').toLowerCase();
          const match = plans.find((p) => {
            const planKey = String(p?.planKey || '').toLowerCase();
            const displayName = String(p?.planName || p?.displayName || '').toLowerCase();
            const interval = String(p?.billingInterval || 'monthly').toLowerCase();
            const matchesName = planKey === targetName || displayName.includes(targetName);
            if (!matchesName) {
              return false;
            }
            return interval === 'monthly';
          }) || plans.find((p) => {
            const planKey = String(p?.planKey || '').toLowerCase();
            const displayName = String(p?.planName || p?.displayName || '').toLowerCase();
            const matchesName = planKey === targetName || displayName.includes(targetName);
            return matchesName;
          });
          if (match) {
            const interval = String(match.billingInterval || 'monthly').toLowerCase() as BillingInterval;
            const monthlyEquivalent = Number(match.monthlyEquivalentCredits ?? match.monthlyCredits ?? 0);
            const creditsPerInterval = Number(match.creditsPerInterval ?? monthlyEquivalent);
            const listPriceNum = Number(match.price ?? 0);
            const currency = (match.currency || 'usd').toUpperCase();
            const dynamicPlan: PlanDetail = {
              key: planParam,
              name: String(match.displayName || match.planName || keyToName[planParam] + ' Plan'),
              originalPrice: Number.isFinite(listPriceNum) && listPriceNum > 0 ? `$${listPriceNum.toFixed(2)}` : '',
              credits: monthlyEquivalent > 0 ? `${monthlyEquivalent.toLocaleString()} credits` : 'credits',
              stripePriceId: String(match.stripePriceID || match.stripePriceId || ''),
              stripeProductId: String(match.stripeProductID || match.stripeProductId || ''),
              billingInterval: interval,
              intervalMonths: Number(match.intervalMonths || (interval === 'yearly' ? 12 : 1)),
              creditsPerInterval,
              monthlyEquivalentCredits: monthlyEquivalent,
              currency,
              defaultCouponId: getDefaultCouponForKey(planParam),
              features: [
                'Translate full novels in minutes',
                'Translate & Optimise metadata and blurbs',
                'Language specific keyword suggestions',
                '5,000 bonus credits when you join',
                'Keep 100% of your royalties',
              ],
            };
            if (!cancelled) setCanonicalPlan(dynamicPlan);
            return;
          }
        }
      } catch (e) {
        // ignore and fall through to starter
      }

      const fallback = (planDetails as Record<string, PlanDetail | undefined>)['starter'];
      if (fallback && !cancelled) setCanonicalPlan(fallback);
    };

    setCanonicalPlan(null);
    loadPlan();
    return () => {
      cancelled = true;
    };
  }, [planParam]);

  // Measure CTA bar height to prevent content being overlapped on mobile
  useEffect(() => {
    const el = ctaBarRef.current;
    const update = () => {
      if (el) setCtaBarHeight(el.offsetHeight || 0);
    };
    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && el) {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    }

    const onResize = () => update();
    window.addEventListener('resize', onResize);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const fetchUserSubscriptions = async (userId: string): Promise<boolean> => {
    try {
      const response = await fetch(
        `${API_BASE}/get-user-subscriptions?userID=${userId}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }
      const data = await response.json();
      return data.subscriptions && data.subscriptions.length > 0;
    } catch (error) {
      // fallback to local user object if API fails
      return checkExistingCustomer(user);
    }
  };

  const checkExistingCustomer = (userData: any): boolean => {
    if (!userData) return false;
    if (userData.stripeCustomerIDs) {
      if (Array.isArray(userData.stripeCustomerIDs) && userData.stripeCustomerIDs.length > 0) return true;
      if (typeof userData.stripeCustomerIDs === 'string' && userData.stripeCustomerIDs.trim() !== '') return true;
    }
    if (userData.stripeCustomerID && typeof userData.stripeCustomerID === 'string' && userData.stripeCustomerID.trim() !== '') {
      return true;
    }
    return false;
  };

  /* ---------- Lifecycle ---------- */
  useEffect(() => {
    if (canonicalPlan) setCheckoutPlan(canonicalPlan.key);
  }, [canonicalPlan, setCheckoutPlan]);

  // Advanced Matching + immediate tracking on mount
  useEffect(() => {
    const run = async () => {
      const externalId = usersSharedState.userID || undefined;
      const email = user?.email || undefined;
      await fbPixel.ensureInit(email, externalId);
      // Fire a PageView so Meta Pixel Helper detects the pixel
      fbPixel.pageview();
      // Optional but useful: ViewContent for checkout view with plan/pricing context
      if (canonicalPlan && effective) {
        fbPixel.event('ViewContent', {
          value: toNumber(effective.displayPrice),
          currency: 'USD',
          plan: canonicalPlan.key,
          source,
          content_ids: [canonicalPlan.key],
          content_type: 'subscription',
          promo_price: effective.displayPrice,
          savings_percent: effective.savingsPercent,
        });
      }
    };
    run();
  }, [usersSharedState.userID, user?.email, canonicalPlan, effective?.displayPrice, effective?.savingsPercent, source]);

  // Auth + eligibility + step control
  useEffect(() => {
    const initialize = async () => {
      await fbPixel.waitForFbPixel();
      if (!isAuth) {
        setCurrentStep(1);
        setIsCheckingEligibility(false);
        return;
      }
      if (usersSharedState.userID) {
        setIsCheckingEligibility(true);
        try {
          const hasSubscriptions = await fetchUserSubscriptions(usersSharedState.userID);
          setIsEligibleForOffer(!hasSubscriptions);
          if (hasSubscriptions) {
            Modal.info({
              title: 'Existing Customer Detected',
              icon: <ExclamationCircleOutlined />,
              content: (
                <div>
                  <p>This special offer is only available for new customers.</p>
                  <p>Manage your plan or purchase credits in your account.</p>
                </div>
              ),
              onOk: () => navigate('/account'),
              okText: 'Manage Your Account',
            });
          } else {
            setCurrentStep(1);
          }
        } finally {
          setIsCheckingEligibility(false);
        }
      }
    };
    const t = setTimeout(initialize, 500);
    return () => clearTimeout(t);
  }, [isAuth, usersSharedState.userID, navigate, showLoginModal]);

  /* ---------- Handlers ---------- */
  const handleCheckout = async (): Promise<void> => {
    if (!isAuth || !usersSharedState.userID || isProcessing || !isEligibleForOffer || isCheckingEligibility) return;
    if (!canonicalPlan || !effective) return;

    const hasSubscriptions = await fetchUserSubscriptions(usersSharedState.userID);
    if (hasSubscriptions) {
      Modal.error({
        title: 'Not Eligible',
        content: 'This offer is only available for new customers.',
        onOk: () => navigate('/manage-books'),
      });
      return;
    }

    setIsProcessing(true);
    setCurrentStep(2);

    try {
      const cookies = getFacebookCookies();
      const initiateCheckoutEventId = fbPixel.generateEventId('InitiateCheckout');
      const anonymousFunnelId = generateRevenueFunnelId();

      await fbPixel.ensureInit(user?.email || undefined, usersSharedState.userID || undefined);
      fbPixel.event(
        'InitiateCheckout',
        {
          value: toNumber(effective.displayPrice),
          currency: 'USD',
          plan: canonicalPlan.key,
          source,
          content_ids: [canonicalPlan.key],
          content_type: 'subscription',
          promo_price: effective.displayPrice,
          savings_percent: effective.savingsPercent,
        },
        initiateCheckoutEventId
      );

      try {
        await trackRevenuePricingIntent({
          anonymousFunnelId,
          planKey: canonicalPlan.key,
          source,
          surface: 'checkout_page',
        });
      } catch (intentError) {
        console.warn('Failed to record revenue pricing intent', intentError);
      }

      const stripe = await getStripePromise();
      if (!stripe) throw new Error('Stripe failed to load');

      const requestBody = buildCheckoutSessionBody({
        plan: canonicalPlan,
        effectivePlan: effective,
        userId: usersSharedState.userID,
        email: user?.email || null,
        source,
        couponParam,
        successUrl: `${window.location.origin}/checkout-success`,
        cancelUrl: `${window.location.origin}/pricing`,
        cookies,
        initiateCheckoutEventId,
        anonymousFunnelId,
        clientUserAgent: navigator.userAgent,
        eventSourceUrl: window.location.href,
        fbTestCode,
      });

      const response = await fetch(`${API_BASE}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(auth.currentUser ? await getAuthHeaders() : {}) },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error('Checkout could not be created');
      const session = await response.json();
      if (!session.id) throw new Error('Checkout session is missing');
      if (session.claimToken) sessionStorage.setItem(`checkout-claim:${session.id}`, session.claimToken);
      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      if (result.error) throw new Error(result.error.message);
    } catch (e) {
      Modal.error({
        title: 'Checkout failed',
        content: 'Please try again or contact support.',
        onOk: () => navigate('/pricing'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGuestCheckout = async (): Promise<void> => {
    if (isProcessing || isCheckingEligibility) return;
    if (!canonicalPlan || !effective) return;
    // Basic email validation
    const email = guestEmail.trim();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailValid) {
      Modal.warning({
        title: 'Enter a valid email',
        content: 'Please enter a valid email address to continue to checkout.',
      });
      return;
    }

    // Continue to server-side offer validation and checkout creation.
    setIsProcessing(true);
    try {
      // The server validates the offer during checkout creation; guest email
      // entry does not perform a separate subscription-history lookup.
      setCurrentStep(2);

      const cookies = getFacebookCookies();
      const initiateCheckoutEventId = fbPixel.generateEventId('InitiateCheckout');
      const anonymousFunnelId = generateRevenueFunnelId();

      await fbPixel.ensureInit(email, null);
      fbPixel.event(
        'InitiateCheckout',
        {
          value: toNumber(effective.displayPrice),
          currency: 'USD',
          plan: canonicalPlan.key,
          source,
          content_ids: [canonicalPlan.key],
          content_type: 'subscription',
          promo_price: effective.displayPrice,
          savings_percent: effective.savingsPercent,
        },
        initiateCheckoutEventId
      );

      try {
        await trackRevenuePricingIntent({
          anonymousFunnelId,
          planKey: canonicalPlan.key,
          source,
          surface: 'checkout_page_guest',
        });
      } catch (intentError) {
        console.warn('Failed to record revenue pricing intent', intentError);
      }

      const stripe = await getStripePromise();
      if (!stripe) throw new Error('Stripe failed to load');

      const requestBody = buildCheckoutSessionBody({
        plan: canonicalPlan,
        effectivePlan: effective,
        userId: null,
        email,
        source,
        couponParam,
        successUrl: `${window.location.origin}/checkout-success`,
        cancelUrl: `${window.location.origin}/pricing`,
        cookies,
        initiateCheckoutEventId,
        anonymousFunnelId,
        clientUserAgent: navigator.userAgent,
        eventSourceUrl: window.location.href,
        fbTestCode,
      });

      const response = await fetch(`${API_BASE}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(auth.currentUser ? await getAuthHeaders() : {}) },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let reasonMsg = 'Failed to create checkout session';
        try {
          const err = await response.json();
          // Server may return 409 with structured detail for ineligible emails
          if (response.status === 409) {
            if (typeof err?.detail === 'string') {
              reasonMsg = err.detail;
            } else if (err?.detail?.message) {
              reasonMsg = err.detail.message;
            } else {
              reasonMsg = 'This offer cannot be applied. Sign in or select standard pricing.';
            }
          }
        } catch {
          // ignore parse errors
        }
        setIsProcessing(false);
        Modal.info({
          title: 'Not Eligible',
          content: reasonMsg,
          okText: 'OK',
        });
        return;
      }

      if (!response.ok) throw new Error('Checkout could not be created');
      const session = await response.json();
      if (!session.id) throw new Error('Checkout session is missing');
      if (session.claimToken) sessionStorage.setItem(`checkout-claim:${session.id}`, session.claimToken);
      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      if (result.error) throw new Error(result.error.message);
    } catch (e) {
      setIsProcessing(false);
      Modal.error({
        title: 'Checkout failed',
        content: 'Please try again or contact support.',
        onOk: () => navigate('/pricing'),
      });
      return;
    } finally {
      // Keep spinner until redirect or error handled above
    }
  };
 
  /* ---------- Helper for arrow styling ---------- */
  const arrowStyle = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: '50%',
    [side]: -48,
    transform: 'translateY(-50%)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
  });

  // Renders "10,000 credits" with tooltip on "credits"
  const renderCreditsWithTooltip = (plan: PlanDetail): React.ReactNode => {
    const amountOnly = plan.credits.replace(/credits/i, '').trim();
    return (
      <>
        {amountOnly}{' '}
        <span className="checkout-credit-tooltip">
          <Tooltip title="1 credit ≈ 1 word">
            <span>
              credits
            </span>
          </Tooltip>
        </span>
      </>
    );
  };

  /* ---------- Render ---------- */
  if (!canonicalPlan || !effective) {
    return (
      <>
      <div className="loading-card-wrapper checkout-loading">
        <LoadingCard label="Loading plan..." />
      </div>
      </>
    );
  }

  const billingSuffix = canonicalPlan.billingInterval === 'yearly' ? 'yr' : 'mo';
  const creditsPeriodLabel = canonicalPlan.billingInterval === 'yearly' ? 'year' : 'month';
  const hasReferralCode = referralCodeInput.trim().length > 0;
  const guestEmailReady = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim());
  const accountReady = isAuth || guestEmailReady;
  const accountStatusText = isAuth
    ? user?.email || 'Signed in'
    : guestEmailReady
      ? guestEmail.trim()
      : 'Email required before Stripe checkout';
  const referralStatusText = referralApplyState === 'applying'
    ? 'Applying referral code'
    : referralApplyState === 'applied'
      ? 'Referral code saved'
      : referralApplyState === 'error'
        ? 'Referral needs attention'
        : hasReferralCode
          ? 'Referral code ready to apply'
          : 'No referral code entered';
  const eligibilityStatusText = isCheckingEligibility
    ? 'Checking eligibility'
    : isEligibleForOffer
      ? 'Offer available'
      : 'Existing customer offer unavailable';
  const paymentStatusText = isProcessing
    ? 'Creating secure Stripe checkout'
    : 'Stripe checkout opens after you start';

  return (
    <>
      {isCheckingEligibility && isAuth ? (
        <div className="loading-card-wrapper checkout-loading">
          <LoadingCard label="Checking your eligibility..." />
        </div>
      ) : (
        <>
          <main className="checkout-page">
            <div className="checkout-shell">
              {/* Show ineligible alert if user has existing subscription */}
              {isAuth && !isEligibleForOffer && (
                <Alert
                  className="checkout-alert"
                  message="Existing Customer"
                  description="This special offer is only available for new customers. As a valued existing customer, please manage your plan or purchase credits in your account."
                  type="info"
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  action={
                    <Button size="small" type="primary" onClick={() => navigate('/account')}>
                      Manage Account
                    </Button>
                  }
                />
              )}

              {/* Header with pricing */}
              <section className="checkout-plan" aria-labelledby="checkout-plan-title" data-tour-id="checkout-plan-summary">
                <AppPageHeader
                  className="checkout-plan__header"
                  kicker="Checkout"
                  title={`${canonicalPlan.name} checkout`}
                  titleId="checkout-plan-title"
                  description="Confirm the plan, account, referral, and payment state before continuing to Stripe."
                  metrics={
                    effective.savingsPercent > 0 ? (
                      <Tag className="checkout-plan__discount">
                        Save {effective.savingsPercent}%
                      </Tag>
                    ) : undefined
                  }
                  secondaryActions={
                    <Button size="small" icon={<QuestionCircleOutlined />} onClick={checkoutTour.start} data-tour-id="checkout-tour-trigger" className="app-tour-trigger">
                      Take tour
                    </Button>
                  }
                />

                <div className="checkout-plan__summary" aria-live="polite">
                  <span className="checkout-plan__name">{canonicalPlan.name}</span>
                  <strong>{effective.displayPrice}</strong>
                  <span>
                    {effective.displayPrice} today. Renews {canonicalPlan.originalPrice}/{billingSuffix}.
                  </span>
                  <Tag className="checkout-plan__credits">
                    {renderCreditsWithTooltip(canonicalPlan)}/{billingSuffix} + 5,000 bonus credits
                  </Tag>
                  <small>Cancel anytime.</small>
                </div>

                <div className="checkout-value-grid" aria-label="Included value">
                  <div className="checkout-value-item">
                    <DollarCircleOutlined className="checkout-value-item__icon checkout-value-item__icon--success" />
                    <span>Keep 100% of your royalties</span>
                  </div>
                  <div className="checkout-value-item">
                    <ThunderboltOutlined className="checkout-value-item__icon checkout-value-item__icon--warning" />
                    <span>No waiting on agencies</span>
                  </div>
                  <div className="checkout-value-item">
                    <GlobalOutlined className="checkout-value-item__icon checkout-value-item__icon--info" />
                    <span>Open new markets</span>
                  </div>
                </div>
              </section>

              <section className="checkout-readiness" aria-labelledby="checkout-readiness-title" aria-live="polite" data-tour-id="checkout-readiness">
                <div className="checkout-section-header">
                  <div>
                    <span className="checkout-section-kicker">Readiness</span>
                    <h2 id="checkout-readiness-title">Before payment</h2>
                  </div>
                  <Tag className={accountReady && isEligibleForOffer ? 'checkout-readiness__tag is-ready' : 'checkout-readiness__tag is-attention'}>
                    {accountReady && isEligibleForOffer ? 'Ready to continue' : 'Action needed'}
                  </Tag>
                </div>

                <div className="checkout-readiness-grid">
                  <div className={`checkout-readiness-row ${accountReady ? 'is-ready' : 'is-attention'}`}>
                    {accountReady ? <CheckCircleOutlined /> : <InfoCircleOutlined />}
                    <div>
                      <strong>Account</strong>
                      <span>{accountStatusText}</span>
                    </div>
                  </div>
                  <div className="checkout-readiness-row is-ready">
                    <CheckCircleOutlined />
                    <div>
                      <strong>Plan</strong>
                      <span>{canonicalPlan.credits} each {creditsPeriodLabel}</span>
                    </div>
                  </div>
                  <div className={`checkout-readiness-row ${referralApplyState === 'error' ? 'is-attention' : 'is-neutral'}`}>
                    {referralApplyState === 'error' ? <ExclamationCircleOutlined /> : <InfoCircleOutlined />}
                    <div>
                      <strong>Referral</strong>
                      <span>{referralStatusText}</span>
                    </div>
                  </div>
                  <div className={`checkout-readiness-row ${isEligibleForOffer ? 'is-ready' : 'is-attention'}`}>
                    {isEligibleForOffer ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                    <div>
                      <strong>Eligibility</strong>
                      <span>{eligibilityStatusText}</span>
                    </div>
                  </div>
                  <div className="checkout-readiness-row is-neutral">
                    <LockOutlined />
                    <div>
                      <strong>Payment</strong>
                      <span>{paymentStatusText}</span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Features, Testimonials, FAQ (below the fold) */}
              <Collapse
                accordion
                bordered={false}
                className="checkout-details"
                defaultActiveKey={!isMobile ? 'features' : undefined}
              >
                <Panel header="What’s included" key="features">
                  <div className="checkout-feature-list">
                    {canonicalPlan.features.map((f) => (
                      <div className="checkout-feature" key={f}>
                        <CheckCircleOutlined />
                        <span>{f}</span>
                      </div>
                    ))}

                    {/* Retailer logos (centered, grayscale) */}
                    <div className="checkout-retailer-logos">
                      <img
                        src="https://commons.wikimedia.org/wiki/Special:FilePath/Amazon%20Kindle%20logo.svg"
                        alt="Amazon Kindle (KDP)"
                      />
                      <img
                        src="https://commons.wikimedia.org/wiki/Special:FilePath/Kobo%20logo.svg"
                        alt="Kobo"
                      />
                      <img
                        src="https://commons.wikimedia.org/wiki/Special:FilePath/Barnes%20%26%20Noble%20logo.svg"
                        alt="Barnes & Noble"
                      />
                      <img
                        src="https://commons.wikimedia.org/wiki/Special:FilePath/Google%20Play%20Books%20icon%20%282023%29.svg"
                        alt="Google Play Books"
                      />
                      <img
                        src="https://commons.wikimedia.org/wiki/Special:FilePath/Apple%20Books%20%28macOS%29.svg"
                        alt="Apple Books"
                      />
                    </div>
                  </div>
                </Panel>

                <Panel header="Success stories from authors" key="reviews">
                  <div className="checkout-testimonials">
                    <Carousel autoplay autoplaySpeed={5000} dots={false} effect="fade" ref={carouselRef}>
                      {testimonials.map((t) => (
                        <div key={t.quote} className="checkout-testimonial">
                          <div className="checkout-testimonial__content">
                            <div className="checkout-testimonial__rating">
                              {[...Array(t.rating)].map((_, i) => (
                                <StarFilled key={i} />
                              ))}
                            </div>
                            <blockquote>{t.quote}</blockquote>
                            <div className="checkout-testimonial__author">
                              <strong>{t.author}</strong>
                              {t.revenue && <Tag color="green">{t.revenue}</Tag>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Carousel>

                    <Button shape="circle" icon={<LeftOutlined />} onClick={() => carouselRef.current?.prev()} style={arrowStyle('left')} />
                    <Button shape="circle" icon={<RightOutlined />} onClick={() => carouselRef.current?.next()} style={arrowStyle('right')} />
                  </div>
                </Panel>

                <Panel header="Frequently asked questions" key="faq">
                  <div className="checkout-faq-list">
                    {faqItems.map((item) => (
                      <div className="checkout-faq" key={item.question}>
                        <strong>{item.question}</strong>
                        <p>{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </Panel>
              </Collapse>

              {/* Sticky CTA + Payment logos + Steps below logos */}
              {isMobile && <div style={{ height: ctaBarHeight + 12 }} aria-hidden="true" />}
              <Affix offsetBottom={isMobile ? 0 : 16} style={{ zIndex: 1000 }}>
                <div
                  ref={ctaBarRef}
                  className={`checkout-action-bar ${isMobile ? 'checkout-action-bar--mobile' : ''}`}
                  data-tour-id="checkout-action-bar"
                >
                  {isProcessing && (
                    <div className="loading-card-wrapper checkout-action-bar__loading">
                      <LoadingCard label="Securing your account..." />
                    </div>
                  )}
                  {!isProcessing && (
                    <>
                      <div className="checkout-action-summary" aria-live="polite">
                        <div>
                          <strong>{effective.displayPrice}</strong>
                          <span>today</span>
                        </div>
                        <div>
                          <strong>{canonicalPlan.originalPrice}/{billingSuffix}</strong>
                          <span>renews after checkout</span>
                        </div>
                      </div>
                      {!isAuth && (
                        <div className="checkout-field">
                          <label>Email for your account</label>
                          <Input
                            type="email"
                            placeholder="Enter your email to get started"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            size="large"
                          />
                        </div>
                      )}
                      <div className="checkout-field">
                        <label>Affiliate code</label>
                        <Input
                          placeholder="Have an affiliate code? Enter it here (optional)"
                          value={referralCodeInput}
                          onChange={handleReferralCodeFieldChange}
                          maxLength={15}
                          disabled={referralApplyState === 'applying'}
                        />
                      </div>
                      {referralMessage && (
                        <Alert
                          className="checkout-referral-alert"
                          type={referralMessage.type}
                          showIcon
                          message={referralMessage.text}
                        />
                      )}
                      <Button
                        type="primary"
                        size="large"
                        block
                        icon={<CreditCardOutlined />}
                        onClick={isAuth ? handleCheckout : handleGuestCheckout}
                        disabled={(isAuth && !isEligibleForOffer) || isCheckingEligibility}
                        loading={isCheckingEligibility}
                        className="checkout-primary-action"
                        data-tour-id="checkout-primary-action"
                      >
                        {isCheckingEligibility
                          ? 'Checking eligibility...'
                          : isAuth && !isEligibleForOffer
                          ? 'Offer not available - existing customer'
                          : isMobile
                          ? `Start for ${effective.displayPrice}`
                          : `Start now for ${effective.displayPrice}`}
                      </Button>

                      {/* Trust microcopy above payment logos */}
                      {isEligibleForOffer && (
                        <>

                          <p className="checkout-trust-copy">
                            Secure checkout by Stripe. We never use your books to train AI.
                          </p>

                          {/* Payment methods under CTA */}
                          {!isMobile && (
                            <div className="checkout-payment-logos">
                                <img
                                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b0/Apple_Pay_logo.svg/220px-Apple_Pay_logo.svg.png"
                                  alt="Apple Pay"
                                />
                                <img
                                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Google_Pay_Logo.svg/256px-Google_Pay_Logo.svg.png"
                                  alt="Google Pay"
                                />
                                <img
                                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/256px-Stripe_Logo%2C_revised_2016.svg.png"
                                  alt="Stripe"
                                />
                                <img
                                  src="https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png"
                                  alt="Visa"
                                />
                                <img
                                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg"
                                  alt="Mastercard"
                                />
                                <img
                                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/240px-American_Express_logo_%282018%29.svg.png"
                                  alt="Amex"
                                />
                            </div>
                          )}

                          {/* Steps moved below payment logos */}
                          {isAuth && !isMobile && (
                            <Steps current={currentStep} size="small" className="checkout-steps">
                              <Step title="Account" icon={<UserOutlined />} />
                              <Step title="Confirm" icon={<CheckCircleOutlined />} />
                              <Step title="Payment" icon={<LockOutlined />} />
                            </Steps>
                          )}
                          <p className="checkout-terms">
                            By continuing, you agree to our
                            {' '}
                            <a href="https://www.scribeshadow.com/terms-conditions" target="_blank" rel="noreferrer">Terms</a>
                            {' '}and{' '}
                            <a href="https://www.scribeshadow.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                            {' '}Need help?{' '}
                            <a href="mailto:support@scribeshadow.com">support@scribeshadow.com</a>
                          </p>
                        </>
                      )}

                      {isAuth && isEligibleForOffer && (
                        <p className="checkout-auth-status">
                          <CheckCircleOutlined /> {user?.email}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </Affix>
            </div>
          </main>
          <AppTour
            open={checkoutTour.open}
            steps={checkoutTourSteps}
            current={checkoutTour.current}
            onChange={checkoutTour.setCurrent}
            onClose={checkoutTour.close}
            onFinish={checkoutTour.close}
          />
        </>
      )}
    </>
  );
};

export default Checkout;
